import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Container,
    Button,
    useTheme,
    useMediaQuery,
    Avatar,
    Tooltip,
    keyframes
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// Define animation keyframes
const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(65, 177, 225, 0.4); }
  50% { box-shadow: 0 0 15px rgba(65, 177, 225, 0.7); }
  100% { box-shadow: 0 0 5px rgba(65, 177, 225, 0.4); }
`;

const logoSpin = keyframes`
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
`;

// Define the pages for navigation
const pages = [
    { name: 'Energy', icon: <ElectricBoltIcon />, link: '/energy-potential' },
    { name: 'Water', icon: <WaterDropIcon />, link: '/water' },
    { name: 'Land', icon: <LocalFloristIcon />, link: '/soil' },
];

const settings = ['Profile', 'Account', 'Dashboard', 'Logout'];

interface HeaderComponentProps {
    showLogo?: boolean;
}

const HeaderComponent: React.FC<HeaderComponentProps> = ({ showLogo = false }) => {
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    // Logo component with enhanced styling
    const Logo = () => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
        }}>
            <Box
                component="div"
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    component="img"
                    src="/EcoFusion_Logo.png"
                    sx={{
                        height: 40,
                        width: 'auto',
                    }}
                    alt="EcoFusion Logo"
                />
            </Box>
        </Box>
    );

    return (
        <AppBar
            position="fixed"
            sx={{
                background: 'rgba(10,25,47,0.85)',
                backdropFilter: 'blur(12px)',
                boxShadow: 'none',
                pointerEvents: 'auto',
                borderBottom: '1px solid rgba(255,255,255,0.05)'
            }}
        >
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    {/* Logo for larger screens */}
                    <Box
                        component={RouterLink}
                        to="/"
                        sx={{
                            mr: 2,
                            display: { xs: 'none', md: 'flex' },
                            textDecoration: 'none',
                        }}
                    >
                        <Logo />
                    </Box>

                    {/* Mobile menu */}
                    <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                '& .MuiPaper-root': {
                                    backgroundColor: 'rgba(10,25,47,0.95)',
                                    backdropFilter: 'blur(12px)',
                                    borderRadius: '10px',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                    border: '1px solid rgba(255,255,255,0.05)',
                                    mt: 1
                                }
                            }}
                        >
                            {pages.map((page) => (
                                <MenuItem
                                    key={page.name}
                                    onClick={handleCloseNavMenu}
                                    component={RouterLink}
                                    to={page.link}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        mr: 1, display: 'flex', alignItems: 'center', color:
                                            page.name === 'Energy' ? theme.palette.warning.main :
                                                page.name === 'Water' ? theme.palette.info.main :
                                                    theme.palette.success.main
                                    }}>
                                        {page.icon}
                                    </Box>
                                    <Typography textAlign="center"
                                        sx={{
                                            color: 'white',
                                            fontWeight: 500
                                        }}
                                    >
                                        {page.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    {/* Logo for mobile view */}
                    <Box
                        component={RouterLink}
                        to="/"
                        sx={{
                            flexGrow: 1,
                            display: { xs: 'flex', md: 'none' },
                            justifyContent: 'center',
                            textDecoration: 'none',
                        }}
                    >
                        <Logo />
                    </Box>

                    {/* Desktop navigation */}
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                        {pages.map((page) => (
                            <Button
                                key={page.name}
                                component={RouterLink}
                                to={page.link}
                                onClick={handleCloseNavMenu}
                                sx={{
                                    my: 2,
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    mx: 2,
                                    py: 1,
                                    px: 2,
                                    borderRadius: '50px',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 0,
                                        height: '2px',
                                        backgroundColor:
                                            page.name === 'Energy' ? theme.palette.warning.main :
                                                page.name === 'Water' ? theme.palette.info.main :
                                                    theme.palette.success.main,
                                        transition: 'width 0.3s ease',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        '&:before': {
                                            width: '80%',
                                        }
                                    }
                                }}
                            >
                                <Box sx={{
                                    mr: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    color:
                                        page.name === 'Energy' ? theme.palette.warning.main :
                                            page.name === 'Water' ? theme.palette.info.main :
                                                theme.palette.success.main
                                }}>
                                    {page.icon}
                                </Box>
                                <Typography sx={{ fontWeight: 500 }}>
                                    {page.name}
                                </Typography>
                            </Button>
                        ))}
                    </Box>

                    {/* User menu */}
                    <Box sx={{ flexGrow: 0 }}>
                        <Tooltip title="Admin settings">
                            <IconButton
                                onClick={handleOpenUserMenu}
                                sx={{
                                    p: 0,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                    }
                                }}
                            >
                                <Avatar sx={{
                                    bgcolor: 'transparent',
                                    border: '2px solid rgba(255,255,255,0.2)',
                                    color: 'white',
                                    boxShadow: '0 0 10px rgba(0,0,0,0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                    },
                                    transition: 'all 0.3s ease'
                                }}>
                                    <AdminPanelSettingsIcon />
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{
                                mt: '45px',
                                '& .MuiPaper-root': {
                                    backgroundColor: 'rgba(10,25,47,0.95)',
                                    backdropFilter: 'blur(12px)',
                                    borderRadius: '10px',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                    border: '1px solid rgba(255,255,255,0.05)'
                                }
                            }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            {settings.map((setting) => (
                                <MenuItem key={setting} onClick={handleCloseUserMenu} sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                    }
                                }}>
                                    <Typography textAlign="center" sx={{ color: 'white' }}>{setting}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default HeaderComponent; 